/**
 * Conversations WhatsApp et SMS du socle (docs/CONVERSATIONS.md, décisions W1 à W14 du 04/11/2026) : règles pures,
 * partagées par l'API (contrôles à l'envoi) et le front (état de la fenêtre, heure d'envoi prévue).
 */
import { z } from 'zod';

export const CANAUX_CONVERSATION = ['whatsapp', 'sms'] as const;
export type CanalConversation = (typeof CANAUX_CONVERSATION)[number];

/** Usage d'un numéro PRODEVAL (W3) : clients (Commerce et SAV) ou support informatique (collaborateurs). */
export const USAGES_NUMERO = ['clients', 'support-it'] as const;
export type UsageNumero = (typeof USAGES_NUMERO)[number];

/** Finalité d'un message et du consentement qui l'autorise (W7). */
export const FINALITES_CONSENTEMENT = ['service', 'marketing'] as const;
export type FinaliteConsentement = (typeof FINALITES_CONSENTEMENT)[number];

export const ETATS_CONSENTEMENT = ['accorde', 'retire'] as const;
export const SOURCES_CONSENTEMENT = [
  'message-entrant', 'demande', 'formulaire', 'accord-ecrit', 'accord-oral', 'stop', 'start', 'import',
] as const;
export type SourceConsentement = (typeof SOURCES_CONSENTEMENT)[number];
/** Sources qui prouvent un accord marketing (W7) : jamais un message reçu ni une demande en cours. */
export const SOURCES_ACCORD_MARKETING: readonly SourceConsentement[] = ['formulaire', 'accord-ecrit', 'accord-oral', 'start', 'import'];

/** Catégorie d'un modèle WhatsApp chez Meta (fixe son prix). */
export const CATEGORIES_META = ['marketing', 'utility', 'authentication'] as const;

export const SENS_MESSAGE = ['entrant', 'sortant'] as const;
export const TYPES_MESSAGE = ['texte', 'modele', 'media', 'interactif', 'systeme'] as const;
/** Statut d'envoi (sortant) ; un message reçu est `recu`. Ordre de progression : a-envoyer → envoye → remis → lu. */
export const STATUTS_ENVOI = ['a-envoyer', 'envoye', 'remis', 'lu', 'echec', 'recu'] as const;
export type StatutEnvoi = (typeof STATUTS_ENVOI)[number];

/** Fenêtre de service client de WhatsApp : 24 h après le dernier message reçu du correspondant. */
export const FENETRE_SERVICE_MS = 24 * 60 * 60 * 1000;

export function fenetreOuverte(dernierEntrantLe: string | Date | null | undefined, maintenant = new Date()): boolean {
  if (!dernierEntrantLe) return false;
  const debut = new Date(dernierEntrantLe).getTime();
  return Number.isFinite(debut) && maintenant.getTime() - debut < FENETRE_SERVICE_MS;
}

/** Fin de la fenêtre (ou null si elle est fermée). */
export function finFenetre(dernierEntrantLe: string | Date | null | undefined, maintenant = new Date()): Date | null {
  if (!fenetreOuverte(dernierEntrantLe, maintenant)) return null;
  return new Date(new Date(dernierEntrantLe as string | Date).getTime() + FENETRE_SERVICE_MS);
}

/**
 * Message « à l'initiative de PRODEVAL » : tout SMS (pas de fenêtre) et tout WhatsApp hors fenêtre (modèle obligatoire).
 * C'est lui que visent les horaires (W8) et les plafonds (W14).
 */
export const estInitiative = (canal: CanalConversation, fenetre: boolean): boolean => canal === 'sms' || !fenetre;

/** Plafonds d'AGORA (W14) : messages à l'initiative de PRODEVAL, par jour (heure de Paris). */
export const PLAFONDS_CONVERSATIONS = { parJourEtFiliale: 300, parJourEtUtilisateur: 40 } as const;

// ------------------------------------------------------------------ horaires d'envoi (W8)
/** Jours ouvrés (1 = lundi … 5 = vendredi), 8 h – 20 h, heure du correspondant. */
export const PLAGE_ENVOI = { jours: [1, 2, 3, 4, 5], debutHeure: 8, finHeure: 20 } as const;

interface PartiesLocales { annee: number; mois: number; jour: number; heure: number; minute: number; jourSemaine: number }

const JOURS: Record<string, number> = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };

function partiesLocales(date: Date, fuseau: string): PartiesLocales {
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
    timeZone: fuseau, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    weekday: 'short', hourCycle: 'h23',
  }).formatToParts(date).map((x) => [x.type, x.value]));
  return {
    annee: Number(p.year), mois: Number(p.month), jour: Number(p.day), heure: Number(p.hour), minute: Number(p.minute),
    jourSemaine: JOURS[p.weekday as string] ?? 1,
  };
}

/** Instant UTC d'une heure locale dans un fuseau (deux corrections suffisent, changements d'heure compris). */
function instantLocal(annee: number, mois: number, jour: number, heure: number, fuseau: string): Date {
  const cible = Date.UTC(annee, mois - 1, jour, heure, 0);
  let t = cible;
  for (let i = 0; i < 2; i++) {
    const l = partiesLocales(new Date(t), fuseau);
    t += cible - Date.UTC(l.annee, l.mois - 1, l.jour, l.heure, l.minute);
  }
  return new Date(t);
}

const dateIso = (l: PartiesLocales) => `${l.annee}-${String(l.mois).padStart(2, '0')}-${String(l.jour).padStart(2, '0')}`;

/**
 * Premier instant d'envoi autorisé à partir de `maintenant` : `maintenant` lui-même s'il est dans la plage, sinon le
 * prochain jour ouvré à 8 h, heure du correspondant. `estFerie(dateIso)` écarte les jours fériés de son pays
 * (calendriers du socle, quand ils existeront).
 */
export function prochainCreneauEnvoi(maintenant: Date, fuseau: string, estFerie: (date: string) => boolean = () => false): Date {
  const ouvre = (l: PartiesLocales) => (PLAGE_ENVOI.jours as readonly number[]).includes(l.jourSemaine) && !estFerie(dateIso(l));
  const l = partiesLocales(maintenant, fuseau);
  if (ouvre(l) && l.heure >= PLAGE_ENVOI.debutHeure && l.heure < PLAGE_ENVOI.finHeure) return maintenant;
  // Avant 8 h un jour ouvré : le jour même ; sinon les jours suivants (au plus 3 semaines de jours fériés enchaînés).
  for (let j = 0; j < 21; j++) {
    const base = new Date(Date.UTC(l.annee, l.mois - 1, l.jour + j, 12));
    const jl = partiesLocales(base, 'UTC');
    const candidat = instantLocal(jl.annee, jl.mois, jl.jour, PLAGE_ENVOI.debutHeure, fuseau);
    if (candidat.getTime() <= maintenant.getTime()) continue;
    if (ouvre(partiesLocales(candidat, fuseau))) return candidat;
  }
  return maintenant;
}

// ------------------------------------------------------------------ mots-clés STOP / START (W7)
const sansAccents = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '');

/** Retrait du consentement service ET marketing sur ce canal (W15), dans les six langues d'AGORA (message réduit à ce seul mot). */
export const MOTS_STOP = ['STOP', 'ARRET', 'STOPP', 'BASTA', 'BAJA', 'PARAR', 'UNSUBSCRIBE', 'DESABONNER', 'ABMELDEN', 'CANCELLA', 'CANCELAR', 'SAIR'] as const;
/** Rétablissement du service et du marketing retirés par STOP (W15). */
export const MOTS_START = ['START', 'UNSTOP', 'REPRENDRE', 'ANMELDEN', 'ISCRIVI', 'ALTA', 'INICIAR'] as const;

export function motCle(texte: string | null | undefined): 'stop' | 'start' | null {
  const mot = sansAccents(String(texte ?? '')).trim().toUpperCase().replace(/[\s.!?¡¿,;:'"-]+/g, '');
  if ((MOTS_STOP as readonly string[]).includes(mot)) return 'stop';
  if ((MOTS_START as readonly string[]).includes(mot)) return 'start';
  return null;
}

// ------------------------------------------------------------------ modèles
/** Variable d'un modèle : identifiant affichable seulement (Meta refuse les retours à la ligne dans une variable). */
export const schemaValeurVariable = z.string().trim().min(1).max(100).regex(/^[^\n\r\t]*$/);
export const schemaVariablesModele = z.record(z.string().regex(/^[a-z][a-zA-Z0-9]*$/), schemaValeurVariable);

/** Remplit `{{variable}}` ; une variable attendue et absente est une erreur de programmation (le service la refuse avant). */
export function remplirModele(texte: string, variables: Record<string, string>): string {
  return texte.replace(/\{\{\s*([a-z][a-zA-Z0-9]*)\s*\}\}/g, (_, nom: string) => variables[nom] ?? `{{${nom}}}`);
}

/** Variables citées par un texte de modèle. */
export const variablesDuTexte = (texte: string): string[] =>
  [...new Set([...texte.matchAll(/\{\{\s*([a-z][a-zA-Z0-9]*)\s*\}\}/g)].map((m) => m[1] as string))];

/** Code de langue d'un modèle chez Meta, pour une langue d'AGORA (Meta distingue le portugais du Brésil). */
export const LANGUE_META: Record<string, string> = { fr: 'fr', en: 'en', de: 'de', it: 'it', es: 'es', pt: 'pt_BR' };

/** Langue du modèle pour un correspondant : la sienne si le modèle l'a, sinon l'anglais, sinon le français. */
export function langueDuModele(textes: Record<string, string>, langue: string | null | undefined): string | null {
  for (const l of [langue, 'en', 'fr']) if (l && textes[l]) return l;
  return Object.keys(textes)[0] ?? null;
}
